import {
  addDays,
  format,
  getWeeksInMonth,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isWeekend,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { Accessor, createEffect, For, JSX, onMount } from "solid-js";
import { EventEditor } from "../models/EventEditor";
import { dayOfWeek, MonthEvent, MonthSpud } from "../models/MonthView";
import { EventHeights } from "./EventHeights";
import "./month.css";

// When loaded as a spud, the monthSpud variable will be declared in the spud page.
declare var monthSpud: MonthSpud;
declare var Trumba: any;
declare var mySpud: any;

// To reserve this variable name so there are no conflicts with $ in the spud code.
declare var $: any;
let a = typeof $ === "undefined" ? null : $;

export interface CalendarViewProps {
  selectedDate?: Date;
  numWeeks?: number;
  isMonthMode?: boolean;
  weekHeight?: number;
  weekBegins?: dayOfWeek;
  headingLevel?: number;
  events?: Accessor<MonthEvent[]>;

  showChevron: boolean;
}

const popupEnabled = (target: HTMLElement) => {
  return typeof Trumba === "object" && target.getAttribute("spudname");
};

const popupMouseOver = (event) => {
  if (popupEnabled(event.currentTarget)) Trumba.Spuds.popupOnMouseOver(event, event.currentTarget);
};

const popupMouseOut = (event) => {
  if (popupEnabled(event.currentTarget)) Trumba.Spuds.popupOnMouseOut(event, event.currentTarget);
};

export default function NewMonthCalendar(props: CalendarViewProps) {
  const seven = [0, 1, 2, 3, 4, 5, 6];

  if (!props.selectedDate) {
    props = {
      selectedDate: new Date(
        Number(monthSpud.date.substring(0, 4)),
        Number(monthSpud.date.substring(4, 6)) - 1,
        Number(monthSpud.date.substring(6, 8))
      ),
      isMonthMode: monthSpud.monthMode,
      numWeeks: monthSpud.numWeeks,
      weekHeight: monthSpud.weekHeight || 0,
      weekBegins: monthSpud.weekBegins || 0,
      headingLevel: monthSpud.headingLevel || 4,
      events: () => monthSpud.events,
      showChevron: monthSpud.iconStyle === 1,
    };
  }

  const minRowHeight = 100;
  const eventHeights = new EventHeights(props.weekHeight, minRowHeight);

  const setMonthWrapWidth = (width: string) => {
    const monthWrap = document.querySelector(".monthWrap") as HTMLElement;
    monthWrap.style.maxWidth = width;
  };

  const setMonthCellPositionUnset = (unset: boolean) => {
    let printStyle = document.getElementById("printStyle");
    if (printStyle) printStyle.innerText = unset ? ".monthCell { position: unset; }" : "";
  };

  function isFirefox() {
    return navigator.userAgent.toLowerCase().indexOf("firefox") >= 0;
  }

  let isPrinting = false;
  onMount(() => {
    // Setup event handlers to force a re-render when window is resized because event heights may have changed.
    if (isFirefox()) {
      // Special case Firefox printing because it calls events differently.
      addEventListener("resize", () => {
        if (!isPrinting) {
          eventHeights.layoutEventBlocks();
          setMonthWrapWidth("");
        }
      });
      addEventListener("beforeprint", () => {
        isPrinting = true;
        eventHeights.forceShowAll = true;
        setMonthCellPositionUnset(true);
        setMonthWrapWidth("7.5in");
        eventHeights.layoutEventBlocks();
        eventHeights.forceShowAll = false;
        setMonthCellPositionUnset(false);
        setMonthWrapWidth("");

        // Redo layout in a setTimeout to make it return to original layout after print.
        // Required in FireFox 104 and before.
        setTimeout(() => {
          eventHeights.layoutEventBlocks();
        }, 1);
      });
      addEventListener("afterprint", () => {
        isPrinting = false;
      });
    } else {
      // The following works in Chromium
      addEventListener("resize", () => {
        // Don't recalc layout on resize event when printing.
        //Messes up when switching between portrait/landscape
        if (!isPrinting) {
          eventHeights.layoutEventBlocks();
        }
      });
      addEventListener("beforeprint", () => {
        isPrinting = true;
        eventHeights.forceShowAll = true;
        setMonthWrapWidth("11in");
        eventHeights.layoutEventBlocks();
        setMonthWrapWidth("");
      });
      addEventListener("afterprint", () => {
        isPrinting = false;
        eventHeights.forceShowAll = false;
        eventHeights.layoutEventBlocks();
      });
    }
  });

  const getStartDate = () => {
    const startDate = props.isMonthMode ? startOfMonth(props.selectedDate) : props.selectedDate;
    return startOfWeek(startDate, { weekStartsOn: props.weekBegins });
  };

  const getSelectedDate = () => {
    return props.selectedDate;
  };

  const getNumWeeks = (): number => {
    return props.isMonthMode ? getWeeksInMonth(getSelectedDate(), { weekStartsOn: props.weekBegins }) : props.numWeeks;
  };

  const today: Date = new Date();

  const renderDayLabels = () => {
    const shortDateFormat = "EEE";
    const dateFormat = "EEEE";
    const days: any[] = [];

    for (let i = 0; i < 7; i++) {
      const day = addDays(getStartDate(), i);
      const longFormat = format(day, dateFormat);
      const shortFormat = format(day, shortDateFormat);
      days.push(
        <th class="weekDayLabel">
          <span class="longLabel">{longFormat}</span>
          <span class="shortLabel">{shortFormat}</span>
        </th>
      );
    }

    return <tr>{days}</tr>;
  };

  const getDisplayWeeks = (): Date[] => {
    eventHeights.clear();
    let days: Date[] = [];

    const viewEndDate = addDays(getStartDate(), getNumWeeks() * 7);
    for (let day = getStartDate(); day < viewEndDate; day = addDays(day, 7)) {
      days.push(day);
    }

    return days;
  };

  const arrowIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="allDayIcon">
      <g>
        <path d="M77.94,256.77c0,17.94-14.33,32.48-32,32.48-17.67,0-32-14.54-32-32.48,0-17.94,14.33-32.48,32-32.48,17.67,0,32,14.54,32,32.48Z" />
        <g>
          <rect x="8.75" y="304.53" width="208.23" height="64" transform="translate(-72.39 639.3) rotate(-130)" />
          <ellipse cx="179.78" cy="416.29" rx="32" ry="31.77" transform="translate(-225.52 212.95) rotate(-40)" />
        </g>
      </g>
      <g>
        <ellipse cx="179.5" cy="95.87" rx="32.48" ry="32" transform="translate(-13.92 32.63) rotate(-10)" />
        <g>
          <rect x="8.46" y="143.62" width="208.23" height="64" transform="translate(-94.32 148.98) rotate(-50)" />
          <ellipse cx="45.66" cy="255.38" rx="31.77" ry="32" transform="translate(-179.32 126.2) rotate(-50)" />
        </g>
      </g>
      <g>
        <g>
          <rect x="45" y="224" width="319.97" height="64" />
          <circle cx="364.97" cy="256" r="32" />
        </g>
        <circle cx="45" cy="256" r="32" />
      </g>
    </svg>
  );

  const endIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="allDayIcon">
      <g>
        <rect x="-114.99" y="224" width="319.97" height="64" transform="translate(-211 301) rotate(-90)" />
        <circle cx="45" cy="415.99" r="32" />
        <circle cx="45" cy="96.01" r="32" />
      </g>
      <rect x="77" y="224" width="287.97" height="64" />
      <circle cx="364.97" cy="256" r="32" />
    </svg>
  );

  const chevronIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="allDayIcon">
      <path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 278.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
    </svg>
  );

  const showLeftIcon = (ev: EventEditor, weekStart: Date): JSX.Element => {
    let returnIcon: any;

    if (isBefore(ev.startDateTime, weekStart)) {
      returnIcon = (
        <span class={`allDayIconWrap chevron ${ev.calendarClass}`}>
          {props.showChevron ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="allDayIcon">
              <path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 278.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="allDayIcon">
              <g>
                <path d="M77.94,256.77c0,17.94-14.33,32.48-32,32.48-17.67,0-32-14.54-32-32.48,0-17.94,14.33-32.48,32-32.48,17.67,0,32,14.54,32,32.48Z" />
                <g>
                  <rect
                    x="8.75"
                    y="304.53"
                    width="208.23"
                    height="64"
                    transform="translate(-72.39 639.3) rotate(-130)"
                  />
                  <ellipse
                    cx="179.78"
                    cy="416.29"
                    rx="32"
                    ry="31.77"
                    transform="translate(-225.52 212.95) rotate(-40)"
                  />
                </g>
              </g>
              <g>
                <ellipse cx="179.5" cy="95.87" rx="32.48" ry="32" transform="translate(-13.92 32.63) rotate(-10)" />
                <g>
                  <rect
                    x="8.46"
                    y="143.62"
                    width="208.23"
                    height="64"
                    transform="translate(-94.32 148.98) rotate(-50)"
                  />
                  <ellipse cx="45.66" cy="255.38" rx="31.77" ry="32" transform="translate(-179.32 126.2) rotate(-50)" />
                </g>
              </g>
              <g>
                <g>
                  <rect x="45" y="224" width="319.97" height="64" />
                  <circle cx="364.97" cy="256" r="32" />
                </g>
                <circle cx="45" cy="256" r="32" />
              </g>
            </svg>
          )}
        </span>
      );
    } else {
      returnIcon = props.showChevron ? (
        <></>
      ) : (
        <span class={`allDayIconWrap arrowLine ${ev.calendarClass}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="allDayIcon">
            <g>
              <rect x="-114.99" y="224" width="319.97" height="64" transform="translate(-211 301) rotate(-90)" />
              <circle cx="45" cy="415.99" r="32" />
              <circle cx="45" cy="96.01" r="32" />
            </g>
            <rect x="77" y="224" width="287.97" height="64" />
            <circle cx="364.97" cy="256" r="32" />
          </svg>
        </span>
      );
    }

    return <>{returnIcon}</>;
  };

  const showRightIcon = (ev: EventEditor, weekEnd: Date): JSX.Element => {
    let returnIcon: any;

    if (isAfter(ev.endDateTime, weekEnd)) {
      returnIcon = (
        <span class={`allDayIconWrap chevron flippedIcon rightIcon ${ev.calendarClass}`}>
          {props.showChevron ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="allDayIcon">
              <path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 278.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="allDayIcon">
              <g>
                <path d="M77.94,256.77c0,17.94-14.33,32.48-32,32.48-17.67,0-32-14.54-32-32.48,0-17.94,14.33-32.48,32-32.48,17.67,0,32,14.54,32,32.48Z" />
                <g>
                  <rect
                    x="8.75"
                    y="304.53"
                    width="208.23"
                    height="64"
                    transform="translate(-72.39 639.3) rotate(-130)"
                  />
                  <ellipse
                    cx="179.78"
                    cy="416.29"
                    rx="32"
                    ry="31.77"
                    transform="translate(-225.52 212.95) rotate(-40)"
                  />
                </g>
              </g>
              <g>
                <ellipse cx="179.5" cy="95.87" rx="32.48" ry="32" transform="translate(-13.92 32.63) rotate(-10)" />
                <g>
                  <rect
                    x="8.46"
                    y="143.62"
                    width="208.23"
                    height="64"
                    transform="translate(-94.32 148.98) rotate(-50)"
                  />
                  <ellipse cx="45.66" cy="255.38" rx="31.77" ry="32" transform="translate(-179.32 126.2) rotate(-50)" />
                </g>
              </g>
              <g>
                <g>
                  <rect x="45" y="224" width="319.97" height="64" />
                  <circle cx="364.97" cy="256" r="32" />
                </g>
                <circle cx="45" cy="256" r="32" />
              </g>
            </svg>
          )}
        </span>
      );
    } else {
      returnIcon = props.showChevron ? (
        <></>
      ) : (
        <span class={`allDayIconWrap arrowLine flippedIcon rightIcon ${ev.calendarClass}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="allDayIcon">
            <g>
              <rect x="-114.99" y="224" width="319.97" height="64" transform="translate(-211 301) rotate(-90)" />
              <circle cx="45" cy="415.99" r="32" />
              <circle cx="45" cy="96.01" r="32" />
            </g>
            <rect x="77" y="224" width="287.97" height="64" />
            <circle cx="364.97" cy="256" r="32" />
          </svg>
        </span>
      );
    }

    return <>{returnIcon}</>;
  };

  const allDayEndTime = (ev: EventEditor, dayInWeek: Date): JSX.Element => {
    const formattedDate = format(ev.endDateTime, "h:mmaaaaa").replace(":00", "");

    if (isSameWeek(ev.endDateTime, dayInWeek) && formattedDate !== "12a") {
      return <span class="endTime">{formattedDate}</span>;
    } else {
      return <></>;
    }
  };

  const setChildrenTabIndex = (tabIndex: string): ((el: HTMLElement) => void) => {
    const localSetTabIndex = (e: HTMLElement) => {
      e.childNodes.forEach((n: HTMLElement) => {
        if (n.nodeType === Node.ELEMENT_NODE) {
          if (n.tagName === "A" || n.tagName === "INPUT" || n.tagName === "BUTTON")
            n.setAttribute("tabindex", tabIndex);
          localSetTabIndex(n);
        }
      });
    };

    return (eventDiv: HTMLElement) => {
      createEffect(() => {
        localSetTabIndex(eventDiv);
      });
    };
  };

  const getEventInnerText = (e: EventEditor): JSX.Element => {
    return (
      <>
        {e.eventActionsHTML && <span class="eventAction" innerHTML={e.eventActionsHTML} />}
        {e.updateIconsHTML && <span class="edPart" innerHTML={e.updateIconsHTML} />}
        {e.timeHTML && <span class="edPart startTime" innerHTML={e.timeHTML} />}
        {e.assetIconsHTML && <span class="edPart alignCenter" innerHTML={e.assetIconsHTML} />}
        {e.titleHTML && <span class="edPart edTitle" innerHTML={e.titleHTML} />}
        {e.extraFieldHTML && <span class="edPart edExtraField" innerHTML={e.extraFieldHTML} />}
      </>
    );
  };

  const eventList = EventEditor.fromMonthEvents(props.events());

  // Get list of events for a single day along with counts.
  const getDayEvents = (
    d: Date,
    weekStart: Date
  ): { events: EventEditor[]; eventCount: number; ongoingCount: number } => {
    let r = { events: [], eventCount: 0, ongoingCount: 0 };
    eventList.forEach((e) => {
      if (e.showsOnThisDay(d)) {
        r.events.push(e);
        if (e.effectivelyAllDay && e.firstInstanceInWeek(weekStart, d)) r.ongoingCount++;
        else r.eventCount++;
      }
    });
    return r;
  };

  return (
    <>
      <style>.weekRow {`{height:${minRowHeight}px}`}</style>
      <style id="printStyle"></style>
      <span id="spanHideShowAlert" class="visually-hidden" role="alert" aria-live="assertive"></span>
      <table class="monthWrap">
        {renderDayLabels()}
        <For each={getDisplayWeeks()}>
          {(weekStart, i) => {
            let weekEnd = addDays(weekStart, 7);
            return (
              <tr
                class="weekRow"
                ontransitionend={(evt) => {
                  (evt.target as HTMLElement).style.transition = "";
                  if (typeof mySpud !== "undefined") mySpud.resize();
                }}
              >
                <For each={seven}>
                  {(delta, day) => {
                    const dayInWeek = addDays(weekStart, delta);

                    // calculate monthClass once for use on multiple elements
                    let monthClass = "";
                    if (props.isMonthMode)
                      monthClass = !isSameMonth(dayInWeek, getSelectedDate())
                        ? "otherMonth"
                        : isWeekend(dayInWeek)
                        ? "weekend"
                        : "";
                    else {
                      monthClass = isWeekend(dayInWeek) ? "weekend" : "weekday";
                      monthClass += isSameMonth(dayInWeek, getSelectedDate()) ? "" : "Alternate";
                    }
                    const sameDayClass = isSameDay(dayInWeek, today) ? "today" : "";

                    const dayEvents = getDayEvents(dayInWeek, weekStart);

                    const todayText = isSameDay(dayInWeek, today) ? " (today)" : "";
                    const eventCount = dayEvents.events.length;
                    const eventCountText =
                      eventCount === 0 ? "" : `, ${eventCount} event${eventCount === 1 ? "" : "s"}`;
                    const dayHeading = format(dayInWeek, "MMMM d") + todayText + eventCountText;

                    const labelID = `dayLabel${i()}-${day()}`;
                    return (
                      <td class={`monthCell ${sameDayClass} ${monthClass}`}>
                        {/* Heading is hidden offscreen for screen reader use only in order to announce full date. */}
                        <span id={labelID} class="visually-hidden" role="heading" aria-level={props.headingLevel + 1}>
                          {dayHeading}
                        </span>
                        <div class="monthDayLabel" aria-hidden="true" ref={eventHeights.setMaxWeekHeight(weekEnd)}>
                          {format(dayInWeek, "d")}
                        </div>
                        <div class="eventsWrapper">
                          <For each={dayEvents.events}>
                            {(e: EventEditor) => {
                              if (e.effectivelyAllDay) {
                                // Include hidden element for ongoing events to list event in screen readers
                                if (!e.firstInstanceInWeek(weekStart, dayInWeek))
                                  return (
                                    <li class="visually-hidden">
                                      <span ref={setChildrenTabIndex("-1")}>Ongoing Event {getEventInnerText(e)}</span>
                                    </li>
                                  );

                                const numDays = e.showsOnDaysWithinWeek(weekStart, weekEnd);
                                const allDayWidth = !!window.matchMedia("print")?.matches
                                  ? `calc(100% *${numDays} + ${(numDays - 1) * 1}px + ${(numDays - 1) * 2}px)`
                                  : `calc(100% *${numDays} + ${(numDays - 1) * 1}px)`;

                                return (
                                  <div
                                    ref={eventHeights.createPositionEffect(dayInWeek, e.endDate, weekEnd)}
                                    class={`eventBlock allDay ${e.calendarClass}`}
                                    style={{
                                      width: allDayWidth,
                                    }}
                                    {...e.popupAttributes}
                                    onmouseover={popupMouseOver}
                                    onmouseout={popupMouseOut}
                                  >
                                    <div class="ebRelativeDIv">
                                      {showLeftIcon(e, weekStart)}
                                      <div class="allDayEvtText">
                                        {getEventInnerText(e)}
                                        {allDayEndTime(e, dayInWeek)}
                                      </div>
                                      {showRightIcon(e, weekEnd)}
                                    </div>
                                  </div>
                                );
                              } else {
                                return (
                                  <div
                                    ref={eventHeights.createPositionEffect(dayInWeek, e.endDate, weekEnd)}
                                    class={`eventBlock singleDay ${e.calendarClass}`}
                                    {...e.popupAttributes}
                                    onmouseover={popupMouseOver}
                                    onmouseout={popupMouseOut}
                                  >
                                    {getEventInnerText(e)}
                                  </div>
                                );
                              }
                            }}
                          </For>
                        </div>
                        <button
                          type="button"
                          class="moreEvents"
                          onclick={(evt) => {
                            const currTar = evt.currentTarget;
                            const list = currTar.previousElementSibling;

                            // Enable animation on weekRow when expanding.
                            // Do it here so animation doesn't happen when view initially loads.
                            // DRH: Animation disabled because events overflow other event rows on expand.  overflow-y: clip fixed this but that broke in FF 124
                            //if (!eventHeights.getWeekShowAll(weekStart))
                            //  list.parentElement.parentElement.style.transition = "all 0.3s ease-out";

                            const evs = list.querySelectorAll(".eventBlock");

                            let focusOnThis: HTMLElement = null;

                            for (let i = 0; i < evs.length; i++) {
                              if (evs[i].classList.contains("hidden-event")) {
                                focusOnThis = evs[i].querySelector("a") as HTMLElement;
                                break;
                              }
                            }

                            window.setTimeout(() => {
                              if (focusOnThis) {
                                focusOnThis.focus();
                                if (typeof mySpud !== "undefined") mySpud.resize();
                              }
                            });

                            // Announce to screen reader that events were shown/hidden
                            const spanHideShowAlert = document.getElementById("spanHideShowAlert");
                            const hiddenCount = currTar.getAttribute("data-hidden-count");
                            if (spanHideShowAlert && hiddenCount !== null) {
                              const plural = hiddenCount !== "1" ? "s" : "";
                              spanHideShowAlert.textContent = "";
                              if (eventHeights.getWeekShowAll(weekStart))
                                spanHideShowAlert.textContent = `${hiddenCount} event${plural} hidden`;
                              else spanHideShowAlert.textContent = `${hiddenCount} event${plural} shown`;
                            }

                            eventHeights.showWeek(weekStart);
                          }}
                        ></button>
                      </td>
                    );
                  }}
                </For>
              </tr>
            );
          }}
        </For>
      </table>
    </>
  );
}
